import { useTranslation } from "react-i18next";
import { NO_DRAG_SELECTOR } from "../../../app/constants";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import {
  AutocompleteInputChangeReason,
  debounce,
  Grid,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { AutocompleteType } from "../../../common/types/autoComplete";
import CustomAutocompleteMulti from "../../../common/components/functional/customAutocomplete/CustomAutocompleteMulti";
import { useCallback, useEffect, useState } from "react";
import { useAppDispatch } from "../../../app/hooks";
import { isFulfilled } from "@reduxjs/toolkit";
import { fetchTalentsAutocompleteWithHr } from "../../talents/talentsSlice";
import { Talent } from "../../talents/talentAPI";
import useIsLoading from "../../../hooks/useIsLoading";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { TalentStatus } from "../../talents/types/common";
import { getName } from "../../../common/helpers/common";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  workLocationId: string | undefined;
  updateTalents: (talents: AutocompleteType[]) => void;
};

export const DIALOG_DRAG_HANDLE_SELECTOR = "talent-pool-add-dialog-drag-handle";

export const TalentPoolAddDialog = ({
  isOpen,
  onClose,
  workLocationId,
  updateTalents,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const isLoading = useIsLoading(["customerWorkLocations"]);
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [talentSearchString, setTalentSearchString] = useState("");
  const [suggestedTalents, setSuggestedTalents] = useState<AutocompleteType[]>(
    [],
  );

  const onInputChange = debounce(
    (term: string, reason?: AutocompleteInputChangeReason) => {
      if (reason === "input") {
        setTalentSearchString(term);
      }
    },
    600,
  );

  const dispatchSearch = useCallback(async () => {
    if (!talentSearchString) return;
    setIsSearchLoading(true);
    const searchResults = await dispatch(
      fetchTalentsAutocompleteWithHr({
        search: talentSearchString,
        pageSize: "500",
      }),
    );

    if (isFulfilled(searchResults)) {
      // Only active talents can be added to pool
      const suitableTalents: AutocompleteType[] = searchResults.payload.data
        .filter((talent) => talent.account.status === TalentStatus.ACTIVE)
        .map((talent: Talent) => ({
          id: talent.id,
          name: getName(talent.account),
        }));
      setSuggestedTalents(suitableTalents);
    }
    setIsSearchLoading(false);
  }, [dispatch, talentSearchString]);

  useEffect(() => {
    dispatchSearch();
  }, [dispatch, dispatchSearch]);

  const defaultValues = {
    talents: [],
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const { control } = methods;

  const saveDisabled =
    isLoading || isSearchLoading || !methods.formState.isValid;

  const emptyAndClose = () => {
    setTalentSearchString("");
    setSuggestedTalents([]);
    methods.reset(defaultValues);
    onClose();
  };

  const onSave = () => {
    if (!workLocationId) return;
    const talents = methods.getValues("talents");
    updateTalents(talents);
    emptyAndClose();
  };

  return (
    <>
      {isLoading && <OverlaySpinner isLoading sx={{ zIndex: "10000" }} />}
      <DialogComponent
        open={isOpen}
        onClose={onClose}
        dragHandleSelector={DIALOG_DRAG_HANDLE_SELECTOR}
      >
        <DialogContentWrapper
          title={t("customers:poolsNS.talentSelection")}
          margin
          onCancel={emptyAndClose}
          onSave={onSave}
          saveDisabled={saveDisabled}
          customSaveButtonLabel={t("common:add")}
        >
          <Grid item container className={NO_DRAG_SELECTOR}>
            <Controller
              name="talents"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <Grid
                  item
                  xs={10}
                  display="flex"
                  alignItems="center"
                  gap="16px"
                >
                  <Typography
                    variant="header"
                    fontWeight={600}
                    noWrap
                    minWidth={150}
                  >
                    {t("order:chooseTalent")}
                  </Typography>
                  <CustomAutocompleteMulti
                    fullWidth
                    disableClientFilter
                    isLoading={isSearchLoading}
                    name="customerUserWorkLocationIds"
                    options={suggestedTalents}
                    onInputChange={(_, value, reason) =>
                      onInputChange(value, reason)
                    }
                    defaultValue={value}
                    onChange={(_: any, newValue: AutocompleteType[]) =>
                      onChange(newValue)
                    }
                    labelLink={(option) => `/home/talents/${option.id}/profile`}
                  />
                </Grid>
              )}
            />
          </Grid>
        </DialogContentWrapper>
      </DialogComponent>
    </>
  );
};
